import { useTranslation } from 'react-i18next'
import { Plus, Settings2, Tag as TagIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { apiError } from '@/lib/api-client'
import { useTags } from '@/hooks/use-settings'
import { useUpdateContact } from '@/hooks/use-contacts'
import { TagChip } from './tag-chip'

/**
 * Nhãn của một khách — CHỈ chọn từ danh mục Cài đặt → Nhãn (cùng danh mục với hộp thư), ghi vào Contact.tags.
 * Muốn nhãn mới thì sang "Quản lý nhãn" (cần quyền tạo nhãn).
 */
export function ContactTagPicker({ contactId, tags, compact = false }: { contactId: string; tags: string[]; compact?: boolean }) {
  const { t } = useTranslation('sharedMisc')
  const { data: catalog = [] } = useTags()
  const update = useUpdateContact()
  const has = (name: string) => tags.some((t) => t.toLowerCase() === name.toLowerCase())

  const toggle = (name: string) => {
    const next = has(name) ? tags.filter((t) => t.toLowerCase() !== name.toLowerCase()) : [...tags, name]
    update.mutate({ id: contactId, data: { tags: next } }, { onError: (e) => toast.error(apiError(e)) })
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((t) => (
        <TagChip key={t} name={t} onRemove={() => toggle(t)} />
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="ghost" size="sm" className={compact ? 'h-6 px-1.5 text-xs text-primary' : 'h-7 px-2 text-xs text-primary'}>
            {tags.length === 0 ? <TagIcon className="h-3 w-3" /> : <Plus className="h-3 w-3" />} {tags.length === 0 ? t('contactTagPicker.addTag') : t('contactTagPicker.tag')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
          {catalog.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">{t('contactTagPicker.noTags')}</p>}
          {catalog.map((t) => (
            <DropdownMenuCheckboxItem key={t.id} checked={has(t.name)} onCheckedChange={() => toggle(t.name)} onSelect={(e) => e.preventDefault()}>
              <TagChip name={t.name} />
            </DropdownMenuCheckboxItem>
          ))}
          <div className="mt-1 border-t px-2 pt-1.5">
            <Link to="/settings?m=tags" className="flex items-center gap-1.5 py-1 text-xs text-primary hover:underline">
              <Settings2 className="h-3.5 w-3.5" /> {t('contactTagPicker.manageTags')}
            </Link>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
